import { type EventEntityType, EventType, type PaginatedResponse } from '@atlas/types'
import { apiClient } from './api.client'

/** An audit event as returned by event-service (`DTOOf<IEvent>`, timestamp ISO). */
export interface EventDTO {
	eventId: string
	type: string
	source: string
	data: Record<string, unknown>
	timestamp: string
	userId?: string
	entityType?: string
	entityId?: string
}

/** Default page size for a work order's audit log. */
export const ACTIVITY_PAGE_SIZE = 20

/**
 * Events a work order's activity does not show: ATLAS does not track Workfront tasks yet.
 * The server leaves them out, so the page and the pager's total count the same events.
 */
export const HIDDEN_WORK_ORDER_EVENT_TYPES: readonly EventType[] = [EventType.WORK_ORDER_EXTERNAL_TASK_CHANGED]

/**
 * event-service client — the read side of the audit log (ADR-0006).
 *
 * Every read returns one bounded page plus the unpaginated total, on all three
 * of the endpoint's filter shapes. An entity-scoped read is newest-first, so
 * page 1 is the most recent activity; a total of 0 comes back as an empty
 * `data` array rather than an empty body, so there is nothing to guard against.
 */
export const activityApi = {
	listForWorkOrder: (
		workOrderId: string,
		params: { page?: number; limit?: number } = {},
	): Promise<PaginatedResponse<EventDTO>> =>
		apiClient
			.get<PaginatedResponse<EventDTO>>('/events', {
				params: {
					'filters[entityType]': 'work_order',
					'filters[entityId]': workOrderId,
					'filters[excludeType]': HIDDEN_WORK_ORDER_EVENT_TYPES.join(','),
					page: params.page ?? 1,
					limit: params.limit ?? ACTIVITY_PAGE_SIZE,
				},
			})
			.then((r) => r.data),
	/** The newest events across the tenant, reverse chronological — the home page's activity feed. */
	listRecent: (limit: number): Promise<PaginatedResponse<EventDTO>> =>
		apiClient.get<PaginatedResponse<EventDTO>>('/events', { params: { limit } }).then((r) => r.data),
	/**
	 * The Administration audit log: every entity, newest first, each filter optional
	 * and honoured on its own.
	 *
	 * An absent filter is left off the query string rather than sent empty, so the
	 * endpoint sees "no filter" and not "a filter matching nothing".
	 */
	listAudit: (params: AuditEventListParams = {}): Promise<PaginatedResponse<EventDTO>> =>
		apiClient
			.get<PaginatedResponse<EventDTO>>('/events', {
				params: {
					...(params.entityType ? { 'filters[entityType]': params.entityType } : {}),
					...(params.entityId ? { 'filters[entityId]': params.entityId } : {}),
					...(params.types?.length ? { 'filters[type]': params.types.join(',') } : {}),
					...(params.page ? { page: params.page } : {}),
					...(params.limit ? { limit: params.limit } : {}),
				},
			})
			.then((r) => r.data),
}

export interface AuditEventListParams {
	entityType?: EventEntityType
	entityId?: string
	/** Only events of these types. */
	types?: EventType[]
	page?: number
	limit?: number
}
